// Service de stockage local pour le mode hors ligne
// Utilise IndexedDB pour stocker les données en attente de synchronisation

const DB_NAME = 'MutuNdeEspoirOffline';
const DB_VERSION = 2; // Incrémenter pour ajouter les nouveaux stores
const STORE_MEMBRES = 'membres';
const STORE_PAIEMENTS = 'paiements';
const STORE_SYNC_QUEUE = 'syncQueue';
const STORE_RECOLTEURS = 'recolteurs';
const STORE_ADMINS = 'admins';
const STORE_GERERS = 'gerers';
const STORE_COTISATIONS = 'cotisations';
const STORE_MONTANTS_MENSUELS = 'montantsMensuels';
const STORE_CENTRES_SANTE = 'centresSante';

let db = null;

// Initialiser la base de données IndexedDB
export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Erreur lors de l\'ouverture de la base de données:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('✅ Base de données IndexedDB initialisée');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Store pour les membres en attente
      if (!db.objectStoreNames.contains(STORE_MEMBRES)) {
        const membresStore = db.createObjectStore(STORE_MEMBRES, { keyPath: 'id', autoIncrement: true });
        membresStore.createIndex('timestamp', 'timestamp', { unique: false });
        membresStore.createIndex('synced', 'synced', { unique: false });
      }

      // Store pour les paiements en attente
      if (!db.objectStoreNames.contains(STORE_PAIEMENTS)) {
        const paiementsStore = db.createObjectStore(STORE_PAIEMENTS, { keyPath: 'id', autoIncrement: true });
        paiementsStore.createIndex('timestamp', 'timestamp', { unique: false });
        paiementsStore.createIndex('synced', 'synced', { unique: false });
        paiementsStore.createIndex('membre_id', 'membre_id', { unique: false });
      }

      // Store pour la queue de synchronisation
      if (!db.objectStoreNames.contains(STORE_SYNC_QUEUE)) {
        const syncStore = db.createObjectStore(STORE_SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('type', 'type', { unique: false });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        syncStore.createIndex('status', 'status', { unique: false });
      }

      // Store pour les récolteurs en attente (admin)
      if (!db.objectStoreNames.contains(STORE_RECOLTEURS)) {
        const recolteursStore = db.createObjectStore(STORE_RECOLTEURS, { keyPath: 'id', autoIncrement: true });
        recolteursStore.createIndex('timestamp', 'timestamp', { unique: false });
        recolteursStore.createIndex('synced', 'synced', { unique: false });
      }

      // Store pour les admins en attente
      if (!db.objectStoreNames.contains(STORE_ADMINS)) {
        const adminsStore = db.createObjectStore(STORE_ADMINS, { keyPath: 'id', autoIncrement: true });
        adminsStore.createIndex('timestamp', 'timestamp', { unique: false });
        adminsStore.createIndex('synced', 'synced', { unique: false });
      }

      // Store pour les gerers en attente
      if (!db.objectStoreNames.contains(STORE_GERERS)) {
        const gerersStore = db.createObjectStore(STORE_GERERS, { keyPath: 'id', autoIncrement: true });
        gerersStore.createIndex('timestamp', 'timestamp', { unique: false });
        gerersStore.createIndex('synced', 'synced', { unique: false });
      }

      // Store pour les cotisations en attente
      if (!db.objectStoreNames.contains(STORE_COTISATIONS)) {
        const cotisationsStore = db.createObjectStore(STORE_COTISATIONS, { keyPath: 'id', autoIncrement: true });
        cotisationsStore.createIndex('timestamp', 'timestamp', { unique: false });
        cotisationsStore.createIndex('synced', 'synced', { unique: false });
        cotisationsStore.createIndex('membre_id', 'membre_id', { unique: false });
      }

      // Store pour les montants mensuels en attente
      if (!db.objectStoreNames.contains(STORE_MONTANTS_MENSUELS)) {
        const montantsStore = db.createObjectStore(STORE_MONTANTS_MENSUELS, { keyPath: 'id', autoIncrement: true });
        montantsStore.createIndex('timestamp', 'timestamp', { unique: false });
        montantsStore.createIndex('synced', 'synced', { unique: false });
      }

      // Store pour les centres de santé en attente
      if (!db.objectStoreNames.contains(STORE_CENTRES_SANTE)) {
        const centresStore = db.createObjectStore(STORE_CENTRES_SANTE, { keyPath: 'id', autoIncrement: true });
        centresStore.createIndex('timestamp', 'timestamp', { unique: false });
        centresStore.createIndex('synced', 'synced', { unique: false });
      }
    };
  });
};

// Obtenir la base de données (l'initialiser si nécessaire)
const getDB = async () => {
  if (!db) {
    await initDB();
  }
  return db;
};

// ============================================
// GESTION DES MEMBRES HORS LIGNE
// ============================================

// Sauvegarder un membre en mode hors ligne
export const saveMembreOffline = async (membreData) => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_MEMBRES, STORE_SYNC_QUEUE], 'readwrite');
    const membresStore = transaction.objectStore(STORE_MEMBRES);
    const syncStore = transaction.objectStore(STORE_SYNC_QUEUE);

    // Générer un ID temporaire unique
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const membreToSave = {
      ...membreData,
      id: tempId,
      timestamp: new Date().toISOString(),
      synced: false,
      offline: true
    };

    // Sauvegarder le membre
    await membresStore.add(membreToSave);

    // Ajouter à la queue de synchronisation
    await syncStore.add({
      type: 'membre',
      data: membreToSave,
      timestamp: new Date().toISOString(),
      status: 'pending'
    });

    console.log('✅ Membre sauvegardé en mode hors ligne:', tempId);
    return { success: true, id: tempId, offline: true };
  } catch (error) {
    console.error('❌ Erreur lors de la sauvegarde hors ligne:', error);
    throw error;
  }
};

// Obtenir tous les membres non synchronisés
export const getUnsyncedMembres = async () => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_MEMBRES], 'readonly');
    const store = transaction.objectStore(STORE_MEMBRES);
    
    return new Promise((resolve, reject) => {
      const results = [];
      // Parcourir tous les membres et filtrer ceux qui ne sont pas synchronisés
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const membre = cursor.value;
          // Filtrer les membres non synchronisés
          if (membre.synced === false || membre.synced === undefined || !membre.synced) {
            results.push(membre);
          }
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des membres non synchronisés:', error);
    return [];
  }
};

// Marquer un membre comme synchronisé
export const markMembreSynced = async (tempId, serverId) => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_MEMBRES, STORE_SYNC_QUEUE], 'readwrite');
    const membresStore = transaction.objectStore(STORE_MEMBRES);
    const syncStore = transaction.objectStore(STORE_SYNC_QUEUE);
    const syncIndex = syncStore.index('type');

    // Mettre à jour le membre
    const getRequest = membresStore.get(tempId);
    getRequest.onsuccess = () => {
      const membre = getRequest.result;
      if (membre) {
        membre.synced = true;
        membre.serverId = serverId;
        membresStore.put(membre);
      }
    };

    // Supprimer de la queue de synchronisation
    const syncRequest = syncIndex.openCursor(IDBKeyRange.only('membre'));
    syncRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.data.id === tempId) {
          cursor.delete();
        }
        cursor.continue();
      }
    };

    console.log('✅ Membre marqué comme synchronisé:', tempId);
  } catch (error) {
    console.error('❌ Erreur lors du marquage du membre comme synchronisé:', error);
  }
};

// ============================================
// GESTION DES PAIEMENTS HORS LIGNE
// ============================================

// Sauvegarder un paiement en mode hors ligne
export const savePaiementOffline = async (paiementData) => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_PAIEMENTS, STORE_SYNC_QUEUE], 'readwrite');
    const paiementsStore = transaction.objectStore(STORE_PAIEMENTS);
    const syncStore = transaction.objectStore(STORE_SYNC_QUEUE);

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const paiementToSave = {
      ...paiementData,
      id: tempId,
      timestamp: new Date().toISOString(),
      synced: false,
      offline: true
    };

    await paiementsStore.add(paiementToSave);

    await syncStore.add({
      type: 'paiement',
      data: paiementToSave,
      timestamp: new Date().toISOString(),
      status: 'pending'
    });

    console.log('✅ Paiement sauvegardé en mode hors ligne:', tempId);
    return { success: true, id: tempId, offline: true };
  } catch (error) {
    console.error('❌ Erreur lors de la sauvegarde du paiement hors ligne:', error);
    throw error;
  }
};

// Obtenir tous les paiements non synchronisés
export const getUnsyncedPaiements = async () => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_PAIEMENTS], 'readonly');
    const store = transaction.objectStore(STORE_PAIEMENTS);
    
    return new Promise((resolve, reject) => {
      const results = [];
      // Parcourir tous les paiements et filtrer ceux qui ne sont pas synchronisés
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const paiement = cursor.value;
          // Filtrer les paiements non synchronisés
          if (paiement.synced === false || paiement.synced === undefined || !paiement.synced) {
            results.push(paiement);
          }
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des paiements non synchronisés:', error);
    return [];
  }
};

// Marquer un paiement comme synchronisé
export const markPaiementSynced = async (tempId, serverId) => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_PAIEMENTS, STORE_SYNC_QUEUE], 'readwrite');
    const paiementsStore = transaction.objectStore(STORE_PAIEMENTS);
    const syncStore = transaction.objectStore(STORE_SYNC_QUEUE);
    const syncIndex = syncStore.index('type');

    const getRequest = paiementsStore.get(tempId);
    getRequest.onsuccess = () => {
      const paiement = getRequest.result;
      if (paiement) {
        paiement.synced = true;
        paiement.serverId = serverId;
        paiementsStore.put(paiement);
      }
    };

    const syncRequest = syncIndex.openCursor(IDBKeyRange.only('paiement'));
    syncRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.data.id === tempId) {
          cursor.delete();
        }
        cursor.continue();
      }
    };

    console.log('✅ Paiement marqué comme synchronisé:', tempId);
  } catch (error) {
    console.error('❌ Erreur lors du marquage du paiement comme synchronisé:', error);
  }
};

// ============================================
// STATISTIQUES HORS LIGNE
// ============================================

// Obtenir le nombre d'éléments en attente de synchronisation
export const getPendingSyncCount = async () => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_SYNC_QUEUE], 'readonly');
    const store = transaction.objectStore(STORE_SYNC_QUEUE);
    const index = store.index('status');
    
    return new Promise((resolve, reject) => {
      const request = index.count(IDBKeyRange.only('pending'));
      request.onsuccess = () => resolve(request.result || 0);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('❌ Erreur lors du comptage des éléments en attente:', error);
    return 0;
  }
};

// Obtenir tous les éléments de la queue de synchronisation
export const getSyncQueue = async () => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_SYNC_QUEUE], 'readonly');
    const store = transaction.objectStore(STORE_SYNC_QUEUE);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('❌ Erreur lors de la récupération de la queue:', error);
    return [];
  }
};

// Nettoyer les données synchronisées (optionnel, pour libérer de l'espace)
export const cleanupSyncedData = async () => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_MEMBRES, STORE_PAIEMENTS], 'readwrite');
    const membresStore = transaction.objectStore(STORE_MEMBRES);
    const paiementsStore = transaction.objectStore(STORE_PAIEMENTS);

    // Supprimer les membres synchronisés de plus de 7 jours
    const membresIndex = membresStore.index('synced');
    const membresRequest = membresIndex.openCursor(IDBKeyRange.only(true));
    membresRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const membre = cursor.value;
        const timestamp = new Date(membre.timestamp);
        const daysAgo = (Date.now() - timestamp.getTime()) / (1000 * 60 * 60 * 24);
        if (daysAgo > 7) {
          cursor.delete();
        }
        cursor.continue();
      }
    };

    // Supprimer les paiements synchronisés de plus de 7 jours
    const paiementsIndex = paiementsStore.index('synced');
    const paiementsRequest = paiementsIndex.openCursor(IDBKeyRange.only(true));
    paiementsRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const paiement = cursor.value;
        const timestamp = new Date(paiement.timestamp);
        const daysAgo = (Date.now() - timestamp.getTime()) / (1000 * 60 * 60 * 24);
        if (daysAgo > 7) {
          cursor.delete();
        }
        cursor.continue();
      }
    };

    console.log('✅ Nettoyage des données synchronisées terminé');
  } catch (error) {
    console.error('❌ Erreur lors du nettoyage:', error);
  }
};

// ============================================
// GESTION DES RÉCOLTEURS HORS LIGNE (ADMIN)
// ============================================

export const saveRecolteurOffline = async (recolteurData) => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_RECOLTEURS, STORE_SYNC_QUEUE], 'readwrite');
    const recolteursStore = transaction.objectStore(STORE_RECOLTEURS);
    const syncStore = transaction.objectStore(STORE_SYNC_QUEUE);

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const recolteurToSave = {
      ...recolteurData,
      id: tempId,
      timestamp: new Date().toISOString(),
      synced: false,
      offline: true
    };

    await recolteursStore.add(recolteurToSave);
    await syncStore.add({
      type: 'recolteur',
      data: recolteurToSave,
      timestamp: new Date().toISOString(),
      status: 'pending'
    });

    console.log('✅ Récolteur sauvegardé en mode hors ligne:', tempId);
    return { success: true, id: tempId, offline: true };
  } catch (error) {
    console.error('❌ Erreur lors de la sauvegarde hors ligne:', error);
    throw error;
  }
};

// ============================================
// GESTION DES ADMINS HORS LIGNE
// ============================================

export const saveAdminOffline = async (adminData) => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_ADMINS, STORE_SYNC_QUEUE], 'readwrite');
    const adminsStore = transaction.objectStore(STORE_ADMINS);
    const syncStore = transaction.objectStore(STORE_SYNC_QUEUE);

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const adminToSave = {
      ...adminData,
      id: tempId,
      timestamp: new Date().toISOString(),
      synced: false,
      offline: true
    };

    await adminsStore.add(adminToSave);
    await syncStore.add({
      type: 'admin',
      data: adminToSave,
      timestamp: new Date().toISOString(),
      status: 'pending'
    });

    console.log('✅ Admin sauvegardé en mode hors ligne:', tempId);
    return { success: true, id: tempId, offline: true };
  } catch (error) {
    console.error('❌ Erreur lors de la sauvegarde hors ligne:', error);
    throw error;
  }
};

// ============================================
// GESTION DES GERERS HORS LIGNE
// ============================================

export const saveGererOffline = async (gererData) => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_GERERS, STORE_SYNC_QUEUE], 'readwrite');
    const gerersStore = transaction.objectStore(STORE_GERERS);
    const syncStore = transaction.objectStore(STORE_SYNC_QUEUE);

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const gererToSave = {
      ...gererData,
      id: tempId,
      timestamp: new Date().toISOString(),
      synced: false,
      offline: true
    };

    await gerersStore.add(gererToSave);
    await syncStore.add({
      type: 'gerer',
      data: gererToSave,
      timestamp: new Date().toISOString(),
      status: 'pending'
    });

    console.log('✅ Gerer sauvegardé en mode hors ligne:', tempId);
    return { success: true, id: tempId, offline: true };
  } catch (error) {
    console.error('❌ Erreur lors de la sauvegarde hors ligne:', error);
    throw error;
  }
};

// ============================================
// GESTION DES COTISATIONS HORS LIGNE
// ============================================

export const saveCotisationOffline = async (cotisationData) => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_COTISATIONS, STORE_SYNC_QUEUE], 'readwrite');
    const cotisationsStore = transaction.objectStore(STORE_COTISATIONS);
    const syncStore = transaction.objectStore(STORE_SYNC_QUEUE);

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const cotisationToSave = {
      ...cotisationData,
      id: tempId,
      timestamp: new Date().toISOString(),
      synced: false,
      offline: true
    };

    await cotisationsStore.add(cotisationToSave);
    await syncStore.add({
      type: 'cotisation',
      data: cotisationToSave,
      timestamp: new Date().toISOString(),
      status: 'pending'
    });

    console.log('✅ Cotisation sauvegardée en mode hors ligne:', tempId);
    return { success: true, id: tempId, offline: true };
  } catch (error) {
    console.error('❌ Erreur lors de la sauvegarde hors ligne:', error);
    throw error;
  }
};

// ============================================
// GESTION DES MONTANTS MENSUELS HORS LIGNE
// ============================================

export const saveMontantMensuelOffline = async (montantData) => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_MONTANTS_MENSUELS, STORE_SYNC_QUEUE], 'readwrite');
    const montantsStore = transaction.objectStore(STORE_MONTANTS_MENSUELS);
    const syncStore = transaction.objectStore(STORE_SYNC_QUEUE);

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const montantToSave = {
      ...montantData,
      id: tempId,
      timestamp: new Date().toISOString(),
      synced: false,
      offline: true
    };

    await montantsStore.add(montantToSave);
    await syncStore.add({
      type: 'montantMensuel',
      data: montantToSave,
      timestamp: new Date().toISOString(),
      status: 'pending'
    });

    console.log('✅ Montant mensuel sauvegardé en mode hors ligne:', tempId);
    return { success: true, id: tempId, offline: true };
  } catch (error) {
    console.error('❌ Erreur lors de la sauvegarde hors ligne:', error);
    throw error;
  }
};

// ============================================
// GESTION DES CENTRES DE SANTÉ HORS LIGNE
// ============================================

export const saveCentreSanteOffline = async (centreData) => {
  try {
    const database = await getDB();
    const transaction = database.transaction([STORE_CENTRES_SANTE, STORE_SYNC_QUEUE], 'readwrite');
    const centresStore = transaction.objectStore(STORE_CENTRES_SANTE);
    const syncStore = transaction.objectStore(STORE_SYNC_QUEUE);

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const centreToSave = {
      ...centreData,
      id: tempId,
      timestamp: new Date().toISOString(),
      synced: false,
      offline: true
    };

    await centresStore.add(centreToSave);
    await syncStore.add({
      type: 'centreSante',
      data: centreToSave,
      timestamp: new Date().toISOString(),
      status: 'pending'
    });

    console.log('✅ Centre de santé sauvegardé en mode hors ligne:', tempId);
    return { success: true, id: tempId, offline: true };
  } catch (error) {
    console.error('❌ Erreur lors de la sauvegarde hors ligne:', error);
    throw error;
  }
};

